import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

import { fetchCurrentUser } from '../api/client'
import type { CurrentUser } from '../api/types'
import { AUTH_MODE, isEntraConfigured, loginRequest } from './config'

export interface AuthState {
  user: CurrentUser | null
  status: 'loading' | 'ready' | 'error'
  message: string | null
  /** The access token for an API call, or null in development. */
  getToken: (options?: { forceRefresh?: boolean }) => Promise<string | null>
  /** Interactive sign in, after a refreshed token was still rejected. */
  reauthenticate: () => Promise<void>
  /** Null in development, where there is no session to end. */
  signOut: (() => void) | null
}

/** Matches the API's fixed development principal. */
const DEV_USER: CurrentUser = {
  oid: '00000000-0000-0000-0000-000000000000',
  name: 'Dev User',
  preferredUsername: 'dev.user@scinsure.com.au',
}

const AuthContext = createContext<AuthState>({
  user: null,
  status: 'loading',
  message: null,
  getToken: async () => null,
  reauthenticate: async () => undefined,
  signOut: null,
})

export function useAuth(): AuthState {
  return useContext(AuthContext)
}

/**
 * Selected once by a build time constant, so the provider tree is stable and
 * MSAL is never even constructed in development.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_MODE === 'entra') {
    return <EntraAuthProvider>{children}</EntraAuthProvider>
  }
  return <DevAuthProvider>{children}</DevAuthProvider>
}

/** Local development: no sign in, no token. The API injects the same principal. */
function DevAuthProvider({ children }: { children: ReactNode }) {
  // Starts with the fake user rather than loading, so there is no sign-in
  // flicker on a loop that never signs in.
  const [user, setUser] = useState<CurrentUser>(DEV_USER)

  useEffect(() => {
    let cancelled = false
    // Confirms the API agrees, and that the dev proxy is wired up. A failure
    // here is not worth reporting: the fake user above already stands.
    fetchCurrentUser({ getToken: async () => null }).then(
      (result) => {
        if (!cancelled) setUser(result)
      },
      () => undefined,
    )
    return () => {
      cancelled = true
    }
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      status: 'ready',
      message: null,
      getToken: async () => null,
      reauthenticate: async () => undefined,
      signOut: null,
    }),
    [user],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** SCI staff SSO. Any staff account may sign in: access is an Entra concern. */
function EntraAuthProvider({ children }: { children: ReactNode }) {
  const { instance, accounts, inProgress } = useMsal()
  const isAuthenticated = useIsAuthenticated()
  const [message, setMessage] = useState<string | null>(
    isEntraConfigured() ? null : 'Sign in is not configured for this environment.',
  )

  // An internal tool has no anonymous state worth rendering, so sign in starts
  // by itself. App shows a branded screen while the redirect resolves.
  useEffect(() => {
    if (!isEntraConfigured()) return
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      instance.loginRedirect(loginRequest).catch(() => {
        setMessage('Sign in could not be started. Reload the page to try again.')
      })
    }
  }, [instance, isAuthenticated, inProgress])

  const activeAccount = instance.getActiveAccount() ?? accounts[0] ?? null

  const getToken = useCallback(
    async ({ forceRefresh = false } = {}): Promise<string | null> => {
      const account = instance.getActiveAccount() ?? accounts[0]
      if (!account) return null
      try {
        const result = await instance.acquireTokenSilent({ ...loginRequest, account, forceRefresh })
        return result.accessToken
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenRedirect({ ...loginRequest, account })
        }
        return null
      }
    },
    [instance, accounts],
  )

  const reauthenticate = useCallback(async () => {
    const account = instance.getActiveAccount() ?? accounts[0]
    await instance.acquireTokenRedirect(account ? { ...loginRequest, account } : loginRequest)
  }, [instance, accounts])

  const signOut = useCallback(() => {
    void instance.logoutRedirect()
  }, [instance])

  const user: CurrentUser | null = activeAccount
    ? {
        oid: activeAccount.localAccountId,
        name: activeAccount.name ?? activeAccount.username,
        preferredUsername: activeAccount.username ?? null,
      }
    : null

  const status: AuthState['status'] = message ? 'error' : isAuthenticated && user ? 'ready' : 'loading'

  const value = useMemo<AuthState>(
    () => ({ user, status, message, getToken, reauthenticate, signOut }),
    [user?.oid, user?.name, user?.preferredUsername, status, message, getToken, reauthenticate, signOut],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
